import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment';

import { TransactionsContext } from '../../../context/transactions/TransactionContext';
import TransactionDetail from '../../../components/ui/TransactionDetail';
import Paginator from '../../../components/ui/Paginator';
import ReportDateRange from '../../../components/ui/ReportDateRange';
import { AuthContext } from '../../../context/auth';
import MerchantPicker from '../../../components/ui/MerchantPicker';
import { canFilterMerchants } from '../../../utils/access';
import topApi from '../../../components/api/ToPApi';


type Filters = {
    dateIni: string;
    dateEnd: string;
    merchantId: number | string;
    transactionId: number | string;
    method: string;
    type: string;
    branch: Branch;
};

// Sucursal de una cadena: toda la cadena, solo la casa matriz o una sucursal
type Branch = 'ALL' | 'MAIN' | number;

interface ChainBranch { id: number; name: string; status?: string }

// Mismos filtros con los que el contexto carga la primera página al montar
const defaultFilters: Filters = {
    dateIni: moment().format("YYYY-MM-DD"),
    dateEnd: moment().add(1, 'day').format("YYYY-MM-DD"),
    merchantId: -1,
    transactionId: -1,
    method: 'Tarjeta',
    type: '-1',
    branch: 'ALL',
};

// Comercio y sucursales que se consultan según el filtro
const chainRequest = (f: Filters): { merchantId: number | string; includeBranches: boolean } =>
    typeof f.branch === 'number'
        ? { merchantId: f.branch, includeBranches: false }
        : { merchantId: f.merchantId, includeBranches: f.branch === 'ALL' };

export const TransactionPage = () => {
    const [buscar, setBuscar] = useState('');
    const [pageActual, setPageActual] = useState<number>(1);
    const { transactions, totalPage, loadTransactions, isLoading, downloadTransactions } = useContext(TransactionsContext);
    const { user } = useContext(AuthContext);

    // Filtros del formulario y filtros con los que se generó la consulta (usados al paginar y descargar)
    const [filters, setFilters] = useState<Filters>(defaultFilters);
    const [appliedFilters, setAppliedFilters] = useState<Filters>(defaultFilters);
    const [isDownloading, setIsDownloading] = useState(false);

    const firstLoad = useRef(true);

    useEffect(() => {
        // La primera página la carga el contexto al montar
        if (firstLoad.current) {
            firstLoad.current = false;
            return;
        }
        const f = appliedFilters;
        const chain = chainRequest(f);
        loadTransactions(pageActual - 1, f.dateIni, f.dateEnd, chain.merchantId, f.transactionId, f.method, f.type, chain.includeBranches);
    }, [pageActual, appliedFilters])

    const updateFilter = (field: keyof Filters, value: any) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    }

    const handleCallback = (start, end) => {
        setFilters(prev => ({ ...prev, dateIni: start.format('YYYY-MM-DD'), dateEnd: end.format('YYYY-MM-DD') }));
    }

    const handleFilter = (e?: React.FormEvent) => {
        e?.preventDefault();
        setAppliedFilters({ ...filters, transactionId: filters.transactionId === '' ? -1 : filters.transactionId });
        setPageActual(1);
    }

    const handleDownload = async () => {
        const f = appliedFilters;
        setIsDownloading(true);
        try {
            const chain = chainRequest(f);
            await downloadTransactions(f.dateIni, f.dateEnd, chain.merchantId, f.transactionId, f.method, f.type, chain.includeBranches);
        } finally {
            setIsDownloading(false);
        }
    }

    const filteredData = useMemo(() => {
        const search = buscar.trim().toLowerCase();
        if (!search) return transactions;
        return transactions.filter((transaction) =>
            [transaction.external_transaction_reference, transaction.auth_code, transaction.pan, transaction.full_name]
                .some(val => String(val ?? '').toLowerCase().includes(search))
        );
    }, [transactions, buscar]);

    const showMerchantSelect = canFilterMerchants(user);

    // Cadena del comercio elegido (o del comercio del usuario): si es casa matriz se filtra por sucursal
    const [chainBranches, setChainBranches] = useState<ChainBranch[]>([]);
    const chainMerchantId = showMerchantSelect ? Number(filters.merchantId) : Number(user?.merchant?.id ?? -1);
    useEffect(() => {
        setFilters(prev => ({ ...prev, branch: 'ALL' }));
        setChainBranches([]);
        if (!(chainMerchantId > 0)) return;
        let active = true;
        topApi.get<{ branches: ChainBranch[] }>(`/merchant/chain?merchantId=${chainMerchantId}`)
            .then(resp => { if (active) setChainBranches(resp.data?.branches ?? []); })
            .catch(() => { if (active) setChainBranches([]); });
        return () => { active = false; };
    }, [chainMerchantId]);

    const hasBranches = chainBranches.length > 0;
    const showMerchantColumn = showMerchantSelect || hasBranches;

    const colSpan = showMerchantColumn ? 9 : 8;

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Consulta de transacciones</h3>
                    <p className="text-muted mb-0">Pagos, devoluciones y anulaciones procesadas.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleDownload} disabled={isDownloading}>
                        {isDownloading
                            ? <><span className="spinner-border spinner-border-sm me-1" /> Descargando...</>
                            : <><i className="bi bi-download me-1" /> Descargar CSV</>}
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <form className="row g-2 align-items-end" onSubmit={handleFilter}>
                        <div className="col-md-6 col-lg-3">
                            <label className="form-label small text-muted mb-1">Rango de fechas</label>
                            <ReportDateRange onChange={handleCallback} minDate={moment().subtract(1, 'year')} />
                        </div>

                        {/* Plataforma y banco: comercio con búsqueda (los usuarios de comercio consultan el suyo y no ven el filtro) */}
                        {showMerchantSelect && (
                            <div className="col-md-6 col-lg-3">
                                <label htmlFor="merchantPicker" className="form-label small text-muted mb-1">Comercio</label>
                                <MerchantPicker id="merchantPicker" value={Number(filters.merchantId)}
                                    onChange={(merchantId) => updateFilter('merchantId', merchantId)} />
                            </div>
                        )}

                        {hasBranches && (
                            <div className="col-md-6 col-lg-3">
                                <label htmlFor="branchSelect" className="form-label small text-muted mb-1">Sucursal</label>
                                <select id="branchSelect" className="form-select" value={String(filters.branch)}
                                    onChange={(e) => updateFilter('branch', e.target.value === 'ALL' || e.target.value === 'MAIN' ? e.target.value : Number(e.target.value))}>
                                    <option value="ALL">Toda la cadena</option>
                                    <option value="MAIN">Solo casa matriz</option>
                                    {chainBranches.map(branch => (
                                        <option key={branch.id} value={branch.id}>{branch.name}{branch.status === 'INA' ? ' (inactiva)' : ''}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="col-6 col-md-4 col-lg-2">
                            <label className="form-label small text-muted mb-1">Método de pago</label>
                            <select
                                id='methodSelect'
                                className='form-select'
                                value={filters.method}
                                onChange={(e) => updateFilter('method', e.target.value)}
                            >
                                <option value="Todos">Todos</option>
                                <option value="Tarjeta">Tarjeta</option>
                                <option value="Efectivo">Efectivo</option>
                                <option value="Sinpe Móvil">Sinpe Móvil</option>
                                <option value="Transferencia">Transferencia</option>
                            </select>
                        </div>

                        <div className="col-6 col-md-4 col-lg-2">
                            <label className="form-label small text-muted mb-1">Tipo</label>
                            <select
                                id='transactionTypeSelect'
                                className='form-select'
                                value={filters.type}
                                onChange={(e) => updateFilter('type', e.target.value)}
                            >
                                <option value="-1">Todos</option>
                                <option value="00">Autorización</option>
                                <option value="01">Rechazada</option>
                                <option value="12">Preautorización</option>
                                <option value="21">Completitud</option>
                                <option value="22">Anulación</option>
                            </select>
                        </div>

                        <div className="col-md-4 col-lg-2">
                            <label className="form-label small text-muted mb-1">N° transacción</label>
                            <input
                                id="transactionIdInput"
                                type="number"
                                className="form-control"
                                placeholder="Opcional"
                                min="0"
                                value={filters.transactionId === -1 ? '' : filters.transactionId}
                                onChange={(e) => updateFilter('transactionId', e.target.value)}
                            />
                        </div>

                        <div className="col-md-4 col-lg-2 d-grid">
                            <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                <i className="bi bi-funnel me-1" /> Filtrar
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Resultados */}
            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    <div className="row g-2 mb-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input
                                    className="form-control"
                                    id="search"
                                    type="text"
                                    placeholder="Buscar por transacción, autorización, tarjeta o usuario..."
                                    value={buscar}
                                    onChange={(e) => setBuscar(e.target.value)}
                                />
                                {buscar && (
                                    <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className='table-responsive'>
                        <table id='transactionTable' className="table table-hover align-middle mb-0">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Transacción</th>
                                    <th>Autorización</th>
                                    <th className="text-end">Monto</th>
                                    <th>Fecha</th>
                                    <th>Tarjeta</th>
                                    <th>Tipo</th>
                                    <th>Modo</th>
                                    {showMerchantColumn && <th>Comercio</th>}
                                    <th>Usuario</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading && !isDownloading ? (
                                    <tr><td colSpan={colSpan} className='text-center py-4'>
                                        <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                    </td></tr>
                                ) : filteredData.length > 0 ? (
                                    filteredData.map(transaction => (
                                        transaction ? <TransactionDetail key={transaction.id} transaction={transaction} showMerchant={showMerchantColumn} /> : null
                                    ))
                                ) : (
                                    <tr><td colSpan={colSpan} className='text-center py-4 text-muted'>
                                        {buscar ? 'No se encontraron transacciones para la búsqueda.' : 'No existen transacciones para los filtros seleccionados.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-end align-items-center mt-3">
                        {totalPage > 1 ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>
        </div>
    )
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    }
};
